import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import {
  InvitationCollectionStatistics,
  InvitationTrackerFilterCardKey,
  InvitationTrackerStatisticCard,
  InvitationTrackerStatisticCardView
} from '@app/v2/event/types/invitation-tracker.type'
import { formatUsdAmount } from '@app/v2/event/utils/invitation-channel-ui.util'
import { InvitationTrackerStatisticCardContentComponent } from './components/invitation-tracker-statistic-card-content/invitation-tracker-statistic-card-content.component'

const ICONS_PATH = 'assets/images/v2/invitation-tracker-statistics'
const DEFAULT_ICON_SIZE_CLASS = 'tw-w-4 tw-h-auto lg:tw-w-[22px]'

const CARD_BASE_CLASS =
  'tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-min-w-[155px] tw-p-2 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-bg-white lg:tw-flex-1 lg:tw-min-w-[174px] lg:tw-p-3 lg:tw-rounded-[10px]'
const CLICKABLE_CARD_CLASS = `${CARD_BASE_CLASS} tw-group/stat-card tw-text-left tw-cursor-pointer tw-transition-colors`
const SELECTED_CARD_CLASS = `${CLICKABLE_CARD_CLASS} tw-ring-[3px] tw-ring-blue-600`
const UNSELECTED_CARD_CLASS = `${CLICKABLE_CARD_CLASS} focus:tw-outline-none focus-visible:tw-border-gray-500 [@media(hover:hover)]:hover:tw-border-gray-500`

const STATISTIC_CARDS: InvitationTrackerStatisticCard[] = [
  {
    key: 'total_invitations',
    label: 'All invitations',
    iconSrc: `${ICONS_PATH}/all-invitations.svg`,
    iconSizeClass: DEFAULT_ICON_SIZE_CLASS,
    iconBgClass: 'tw-bg-blue-50',
    isClickable: false
  },
  { key: 'total_cost', label: 'Total cost', iconSrc: `${ICONS_PATH}/total-cost.svg`, iconSizeClass: DEFAULT_ICON_SIZE_CLASS, iconBgClass: 'tw-bg-teal-50', isClickable: false },
  { key: 'total_pending', label: 'Pending', iconSrc: `${ICONS_PATH}/pending.svg`, iconSizeClass: DEFAULT_ICON_SIZE_CLASS, iconBgClass: 'tw-bg-yellow-50', isClickable: true },
  {
    key: 'total_registered',
    label: 'Registered',
    iconSrc: `${ICONS_PATH}/registered.svg`,
    iconSizeClass: DEFAULT_ICON_SIZE_CLASS,
    iconBgClass: 'tw-bg-green-50',
    isClickable: true
  },
  {
    key: 'total_declined',
    label: 'Declined',
    iconSrc: `${ICONS_PATH}/declined.svg`,
    iconSizeClass: 'tw-w-[14px] tw-h-auto lg:tw-w-[18px]',
    iconBgClass: 'tw-bg-red-50',
    isClickable: true
  },
  {
    key: 'total_bounced',
    label: 'Bounced',
    iconSrc: `${ICONS_PATH}/bounced.svg`,
    iconSizeClass: 'tw-w-[18px] tw-h-auto lg:tw-w-6',
    iconBgClass: 'tw-bg-orange-50',
    isClickable: true
  },
  {
    key: 'total_opt_out',
    label: 'Opted out',
    iconSrc: `${ICONS_PATH}/opted-out.svg`,
    iconSizeClass: 'tw-h-[18px] tw-w-auto lg:tw-h-6',
    iconBgClass: 'tw-bg-red-50',
    isClickable: true
  }
]

@Component({
  selector: 'app-invitation-tracker-statistics',
  standalone: true,
  imports: [NgClass, InvitationTrackerStatisticCardContentComponent],
  templateUrl: './invitation-tracker-statistics.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitationTrackerStatisticsComponent {
  public readonly statistics = input<InvitationCollectionStatistics | null>(null)
  public readonly isLoading = input(false)
  public readonly selectedCardKey = input<InvitationTrackerFilterCardKey | null>(null)
  public readonly cardClicked = output<InvitationTrackerFilterCardKey>()
  public readonly isSkeleton = computed(() => this.isLoading() && !this.statistics())

  public readonly cards = computed<InvitationTrackerStatisticCardView[]>(() => {
    const statistics = this.statistics()
    return STATISTIC_CARDS.map((card) => {
      const isSelected = card.isClickable && card.key === this.selectedCardKey()
      return { ...card, isSelected, cardClass: this._resolveCardClass(card, isSelected), value: this._formatValue(card.key, statistics?.[card.key] ?? 0) }
    })
  })

  /**
   * Emit the clicked card key so the parent can apply its filter.
   * @param key {InvitationTrackerFilterCardKey} - The clicked card key.
   */
  public onCardClick(key: InvitationTrackerFilterCardKey): void {
    this.cardClicked.emit(key)
  }

  /**
   * Resolve the card container classes for its clickable and selected state.
   * @param card {InvitationTrackerStatisticCard} - The card.
   * @param isSelected {boolean} - Whether the card is selected.
   * @returns {string} - The container classes.
   */
  private _resolveCardClass(card: InvitationTrackerStatisticCard, isSelected: boolean): string {
    if (!card.isClickable) return CARD_BASE_CLASS
    return isSelected ? SELECTED_CARD_CLASS : UNSELECTED_CARD_CLASS
  }

  /**
   * Formats a statistic value for display (currency for cost, grouped integer otherwise).
   * @param key {keyof InvitationCollectionStatistics} - Statistic key.
   * @param value {number} - Raw value from the API.
   * @returns Display string.
   */
  private _formatValue(key: keyof InvitationCollectionStatistics, value: number): string {
    if (key === 'total_cost') return formatUsdAmount(value)
    return (Number(value) || 0).toLocaleString('en-US')
  }
}
